<template>
  <div v-if="!isEmptyData || pageLoading" class="inbound-report">
    <InsightHeader
      class="header"
      :loading="pageLoading"
      :reporting-message="reportingMessage"
      @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
    />
    <div class="inbound-report-content">
      <InboundReportApplicationsWidget
        class="inbound-applications"
        :data="flatListOfSources"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <InboundReportHiresWidget
        class="inbound-hires"
        :data="flatListOfSources"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <div class="d-flex justify-content-start align-center switch-container">
        <SwitchLoader v-if="pageLoading" />
        <DpplSwitch
          v-else
          left="Paid vs Free"
          right="General vs Niche"
          :value="!isGeneralVsNiche"
          :width="260"
          new-version
          @change="handleToggleGeneralVsNiche"
        />
      </div>
      <InboundReportSummary
        class="summary mb-0"
        :loading="pageLoading"
        :data="categoryTotals"
        :tooltips="tooltips"
        :project-name="projectName"
        :date-range="dateRange"
        :is-general-vs-niche="isGeneralVsNiche"
      />
      <InboundReportTable
        class="table"
        :error="error"
        :loading="pageLoading"
        :empty-data="isEmptyData"
        :data="data"
        :other-inbound-rows="otherInboundRows"
        :first-group-rows="firstGroupRows"
        :second-group-rows="secondGroupRows"
        :table-totals="tableTotals"
        :downloading="downloading"
        @download-csv="downloadInboundTable"
      />
    </div>
    <FilterExportPreview class="mt-4" />
  </div>
  <NoDataForWidget v-else />
</template>

<script setup>
import {
  onMounted, onBeforeUnmount, computed, watch, ref,
} from 'vue';
import { useFilters } from '@/stores/filters/filters';
import { useRoute, useRouter } from 'vue-router';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useAppBase } from '@/stores/app/base';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { useInboundReport } from '@/stores/insights/inboundReport';
import InboundReportApplicationsWidget from '@/components/insights/SourceInsights/InboundReport/InboundReportApplicationsWidget.vue';
import InboundReportTable from '@/components/insights/SourceInsights/InboundReport/InboundReportTable.vue';
import { useInsights } from '@/stores/insights/insights';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import InboundReportHiresWidget from '@/components/insights/SourceInsights/InboundReport/InboundReportHiresWidget.vue';
import InboundReportSummary from '@/components/insights/SourceInsights/InboundReport/InboundReportSummary.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useTooltips } from '@/stores/insights/tooltips';
import DpplSwitch from '@/components/datapeople/DpplSwitch.vue';
import SwitchLoader from '@/components/insights/ProcessInsights/DemographicSurveyReport/skeletonLoaders/SwitchLoader.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';
import { useTracking } from '@/stores/tracking/tracking';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const emit = defineEmits(['track-page-load']);

const {
  isEmptyData,
  data,
  otherInboundRows,
  firstGroupRows,
  secondGroupRows,
  error,
  tableTotals,
  flatListOfSources,
  categoryTotals,
  isGeneralVsNiche,
} = storeToRefs(useInboundReport());
const { pageLoading } = storeToRefs(useAppLoading());
const { authData } = storeToRefs(useAppAuthData());
const { reportingMessage } = storeToRefs(useInsights());
const { tooltips } = storeToRefs(useTooltips());
const { dateRange } = storeToRefs(useDatePicker());

const { loadFilters, setFilterConfig } = useFilters();
const { setDatePickerVariant, resetDateRange } = useDatePicker();
const {
  loadInboundReport,
  clearData,
  toggleGeneralVsNiche,
  updateIsGeneralVsNiche,
  downloadInboundGeneralVsNicheTable,
  downloadInboundPaidVsFreeTable,
} = useInboundReport();
const { showFilterBarSettings } = useAppBase();
const { setReportingMessage } = useInsights();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const route = useRoute();
const router = useRouter();

const downloading = ref(false);

const projectName = computed(() => authData.value.projectName);

const loadPage = async (params) => Promise.all([
  loadFilters(params),
  loadInboundReport(params),
]);

const downloadInboundTable = async () => {
  downloading.value = true;

  if (isGeneralVsNiche.value) {
    await downloadInboundGeneralVsNicheTable({
      dateRange: dateRange.value,
      projectName: projectName.value,
      routeQuery: route.query || '',
      router,
    });

    useTracking().insightsDownloadReport({
      reportName: route.name,
      downloadType: 'Individual Widgets',
      downloadLocation: 'Individual Widget',
      fileType: 'CSV',
      widgetName: 'General vs Niche Data Table',
    });
  } else {
    await downloadInboundPaidVsFreeTable({
      dateRange: dateRange.value,
      projectName: projectName.value,
      routeQuery: route.query || '',
      router,
    });

    useTracking().insightsDownloadReport({
      reportName: route.name,
      downloadType: 'Individual Widgets',
      downloadLocation: 'Individual Widget',
      fileType: 'CSV',
      widgetName: 'Paid vs Free Data Table',
    });
  }

  downloading.value = false;
};

const handleToggleGeneralVsNiche = () => {
  toggleGeneralVsNiche({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', router });
};

const pngWidgetOptions = computed(() => {
  const options = {
    'top-sources-by-application': 'Graph: Source Distribution of Apps',
    'top-sources-by-hire': 'Graph: Source Distribution of Hires',
    general_applications: 'Metric: % Apps from General Sources',
    general_hired: 'Metric: % Hires from General Sources',
    niche_applications: 'Metric: % Apps from Niche Sources',
    niche_hired: 'Metric: % Hires from Niche Sources',
    free_applications: 'Metric: % Apps from Free Sources',
    free_hired: 'Metric: % Hires from Free Sources',
    paid_applications: 'Metric: % Apps from Paid Sources',
    paid_hired: 'Metric: % Hires from Paid Sources',
    'inbound-report-table': isGeneralVsNiche.value
      ? 'Table: Inbound Data - General/Niche'
      : 'Table: Inbound Data - Free/Paid',
  };

  if (isGeneralVsNiche.value) {
    delete options.free_applications;
    delete options.free_hired;
    delete options.paid_applications;
    delete options.paid_hired;
  } else {
    delete options.general_applications;
    delete options.general_hired;
    delete options.niche_applications;
    delete options.niche_hired;
  }

  return options;
});

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  emit('track-page-load', { page: 'Inbound Report' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'source',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');
  showFilterBarSettings({ route: route.name, options: [] });

  setShareConfig({
    shareable: true,
    showReportImageButton: true,
  });

  setExportConfig({
    csvExport: true,
    pngExport: true,
    pngWidgetOptions: pngWidgetOptions.value,
    csvOptions: [
      {
        label: 'Paid vs Free Data Table',
        csvDownloadFunction: async () => downloadInboundPaidVsFreeTable({
          dateRange: dateRange.value,
          projectName: projectName.value,
          routeQuery: route.query || '',
          router,
        }),
      },
      {
        label: 'General vs Niche Data Table',
        csvDownloadFunction: async () => downloadInboundGeneralVsNicheTable({
          dateRange: dateRange.value,
          projectName: projectName.value,
          routeQuery: route.query || '',
          router,
        }),
      },
    ],
  });

  let isToggle = false;

  if (route.query?.general_vs_niche) {
    updateIsGeneralVsNiche(
      route.query.general_vs_niche === 'true',
    );

    isToggle = true;
  }

  await loadPage({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', isToggle });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  resetDateRange();

  clearData();

  resetExportConfig();

  resetShareConfig();
});

watch(() => pngWidgetOptions.value, (newValue) => {
  setExportConfig({
    pngWidgetOptions: newValue,
  });
});

watch(() => isGeneralVsNiche.value, (newValue) => {
  router.replace({
    query: {
      ...route.query,
      general_vs_niche: newValue,
    },
  });
}, { immediate: true });
</script>

<style lang="less" scoped>
.inbound-report {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 16px;
}

.inbound-report-content {
  width: 100%;
  display: grid;
  grid-template-areas:
    "inboundApplications inboundHires"
    "switch switch"
    "summary summary"
    "table table";
  grid-template-rows: 1fr auto auto;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.switch-container {
  grid-area: switch;
}

.summary {
  grid-area: summary;
}

.inbound-applications {
  grid-area: inboundApplications;
}

.inbound-hires {
  grid-area: inboundHires;
}

.table {
  grid-area: table;
}
</style>
